import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import ClientPicker from "./ClientPicker";
import {
  confirmPartnerDocumentRequest,
  createPartnerDocumentRequest,
  uploadPartnerDocumentFile,
} from "../../api/endpoints/documents";
import {
  buildUploadPayload,
  emptyUploadRow,
  pairRowsWithItems,
  validateUploadRows,
} from "./newRequestHelpers";

const errorMessage = (err, fallback) =>
  err?.response?.data?.errorMessage || err?.response?.data?.message || err?.message || fallback;

/**
 * The partner already holds the files: name each document, attach its files, and optionally send
 * the set straight to the office. Under the hood it is a request the partner fills in themselves.
 */
/** Pass a client to fix it (a client's page); leave it out to choose one (the Documents page). */
const UploadDocumentsDialog = ({
  clientId: fixedClientId,
  clientName: fixedClientName,
  open,
  onClose,
  onCreated,
}) => {
  const [picked, setPicked] = useState(null);
  const clientId = fixedClientId ?? picked?.clientId;
  const clientName = fixedClientName ?? picked?.name;
  const [title, setTitle] = useState("");
  const [rows, setRows] = useState(() => [emptyUploadRow()]);
  const [sendNow, setSendNow] = useState(false);
  const [formError, setFormError] = useState(null);
  const [progress, setProgress] = useState(null);

  const reset = () => {
    setTitle("");
    setRows([emptyUploadRow()]);
    setSendNow(false);
    setFormError(null);
    setPicked(null);
    setProgress(null);
  };

  const totalFiles = rows.reduce((total, row) => total + row.files.length, 0);

  const mutation = useMutation(
    async () => {
      const created = await createPartnerDocumentRequest(
        clientId,
        buildUploadPayload({ title, rows })
      );
      const pairs = pairRowsWithItems(rows, created.items || []);
      let done = 0;
      setProgress({ done, total: totalFiles });
      // One at a time: the progress count stays honest and a failure names the file that failed.
      for (const { row, itemId } of pairs) {
        for (const file of row.files) {
          try {
            await uploadPartnerDocumentFile(created.id, itemId, file);
          } catch (err) {
            const failure = new Error(`"${file.name}" did not upload: ${errorMessage(err, "")}`);
            failure.request = created;
            throw failure;
          }
          done += 1;
          setProgress({ done, total: totalFiles });
        }
      }
      if (sendNow) await confirmPartnerDocumentRequest(created.id);
      return created;
    },
    {
      onSuccess: (created) => {
        toast.success(sendNow ? "Uploaded and sent to the office" : "Documents uploaded");
        onCreated?.(created);
        reset();
        onClose();
      },
      onError: (err) => {
        toast.error(errorMessage(err, "Upload failed"));
        // The request exists even when a file failed; show it so the partner can finish there.
        if (err?.request) onCreated?.(err.request);
        setProgress(null);
      },
    }
  );

  const close = () => {
    if (mutation.isLoading) return;
    reset();
    onClose();
  };

  const updateRow = (key, changes) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...changes } : row)));

  const addFiles = (row, fileList) => {
    const chosen = Array.from(fileList || []);
    if (!chosen.length) return;
    // A single file with no name yet names the document after the file.
    const label =
      row.label || (chosen.length === 1 ? chosen[0].name.replace(/\.[^.]+$/, "") : row.label);
    updateRow(row.key, { files: [...row.files, ...chosen], label });
  };

  const submit = () => {
    const error = clientId ? validateUploadRows(rows) : "Choose a client first.";
    setFormError(error);
    if (!error) mutation.mutate();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>
        {clientName ? `Upload documents for ${clientName}` : "Upload documents"}
      </DialogTitle>
      <DialogContent>
        {fixedClientId ? null : (
          <ClientPicker
            value={picked}
            onChange={setPicked}
            disabled={mutation.isLoading}
            error={!clientId && formError ? formError : null}
          />
        )}
        <TextField
          label="Title (optional)"
          placeholder="e.g. Enrolment documents"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          fullWidth
          size="small"
          margin="dense"
        />

        <ul className="list-none m-0 mt-3 p-0 space-y-2">
          {rows.map((row, index) => (
            <li key={row.key} className="rounded-xl border border-slate-200 p-3">
              <div className="flex items-start gap-2">
                <TextField
                  label={`Document name ${index + 1}`}
                  placeholder="e.g. Passport"
                  value={row.label}
                  onChange={(event) => updateRow(row.key, { label: event.target.value })}
                  fullWidth
                  size="small"
                />
                <IconButton
                  size="small"
                  aria-label="Remove document"
                  disabled={rows.length === 1}
                  onClick={() => setRows((current) => current.filter((r) => r.key !== row.key))}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </div>

              {row.files.length ? (
                <ul className="list-none m-0 mt-2 p-0 space-y-1">
                  {row.files.map((file, fileIndex) => (
                    <li
                      key={`${file.name}-${fileIndex}`}
                      className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-1.5 text-sm text-slate-700"
                    >
                      <span className="truncate">{file.name}</span>
                      <IconButton
                        size="small"
                        aria-label={`Remove ${file.name}`}
                        onClick={() =>
                          updateRow(row.key, {
                            files: row.files.filter((_, i) => i !== fileIndex),
                          })
                        }
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </li>
                  ))}
                </ul>
              ) : null}

              <label className="mt-2 flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-teal-300 px-3 py-2.5 text-xs font-semibold text-teal-700 cursor-pointer hover:bg-teal-50">
                <input
                  type="file"
                  multiple
                  className="sr-only"
                  onChange={(event) => {
                    addFiles(row, event.target.files);
                    event.target.value = "";
                  }}
                />
                <UploadFileOutlinedIcon sx={{ fontSize: 18 }} />
                {row.files.length ? "Add more files" : "Choose files"}
              </label>
            </li>
          ))}
        </ul>
        <Button
          size="small"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => setRows((current) => [...current, emptyUploadRow()])}
          sx={{ mt: 1 }}
        >
          Add another document
        </Button>

        <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2">
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={sendNow}
                onChange={(event) => setSendNow(event.target.checked)}
              />
            }
            label={<span className="text-sm">Send to the office for processing straight away</span>}
          />
        </div>

        {formError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {formError}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={mutation.isLoading}>
          Cancel
        </Button>
        <Button variant="contained" disableElevation onClick={submit} disabled={mutation.isLoading}>
          {mutation.isLoading
            ? progress
              ? `Uploading ${progress.done} of ${progress.total}…`
              : "Creating…"
            : sendNow
            ? "Upload and send"
            : `Upload${totalFiles ? ` ${totalFiles} file${totalFiles === 1 ? "" : "s"}` : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadDocumentsDialog;
